import { apiClient } from './client';
import { unwrapList } from './unwrap';
import type { JudgeCompetition, JudgingProgress, JudgingScore, MyResult, PagedResult, SubmissionRanking, SubmitScoreRequest } from './types';

export const judgingApi = {
  listScoresForSubmission: (submissionId: string) =>
    apiClient
      .get(`/judging/submissions/${submissionId}/scores`)
      .then((r) => unwrapList<JudgingScore>(r.data)) as Promise<PagedResult<JudgingScore>>,

  /** GET /judging/competitions/{competitionId}/ranking - real average-score ranking, not a client-side guess. */
  getRanking: (competitionId: string) =>
    apiClient
      .get(`/judging/competitions/${competitionId}/ranking`)
      .then((r) => unwrapList<SubmissionRanking>(r.data)) as Promise<PagedResult<SubmissionRanking>>,

  /**
   * Competitions the calling judge is assigned to (Judge is assigned to a whole
   * competition, not individual submissions - see GET /judging/my-competitions).
   * The judge portal drills into a chosen competition's submissions via the
   * existing submissions/ranking endpoints.
   */
  listMyCompetitions: () =>
    apiClient
      .get('/judging/my-competitions')
      .then((r) => unwrapList<JudgeCompetition>(r.data)) as Promise<PagedResult<JudgeCompetition>>,

  /**
   * Submits (or updates) the calling judge's score for a submission. There is
   * no separate update route - POST /judging/scores is an idempotent upsert
   * per JudgingController.SubmitScore's doc comment, so "submit" and "change
   * my existing score" are both just this same call.
   */
  submitScore: (payload: SubmitScoreRequest) =>
    apiClient.post<JudgingScore>('/judging/scores', payload).then((r) => r.data),

  /** GET /judging/competitions/{competitionId}/my-scores - only the calling judge's own scores in this competition. */
  getMyScores: (competitionId: string) =>
    apiClient
      .get(`/judging/competitions/${competitionId}/my-scores`)
      .then((r) => unwrapList<JudgingScore>(r.data)) as Promise<PagedResult<JudgingScore>>,

  /** GET /judging/my-results - the caller's own entries with competition, status and (after judging) score, place and awards. */
  getMyResults: () =>
    apiClient.get('/judging/my-results').then((r) => unwrapList<MyResult>(r.data)) as Promise<PagedResult<MyResult>>,

  /** GET /judging/competitions/{competitionId}/progress - how many of this competition's accepted submissions the calling judge has scored so far. */
  getProgress: (competitionId: string) =>
    apiClient.get<JudgingProgress>(`/judging/competitions/${competitionId}/progress`).then((r) => r.data),
};
